// @flow
import type {Element} from 'react'
import {pipe, concat, curry, uniqBy, prop, map, reject, isNil, sortBy} from 'ramda'
import React, {useCallback, useEffect, useState} from 'react'
import RailsSelect from 'components/ui/forms/RailsSelect/StaticRailsSelect'
import RoleCache from 'utils/OrgChartDataUtils/RoleCache'
import type {Option, PlaceholderData} from '../RailsSelect/types'

const ROLE_SELECT_WRAPPER_CLASS = 'js-slick-role-select'

export type Props = $ReadOnly<{
  defaultValue?: Object,
  id: string,
  isClearable?: boolean,
  name: string,
  pageContext: string,
  parentRoleId?: string,
  personRoleIds?: Array<string>,
  organizationId: string,
  showCircleName: boolean,
  includeAllCircleMembers?: boolean,
  includeIndividualInitiative?: boolean,
  individualInitiativeLabel?: string,
  options: Array<Option>,
  placeholderData?: PlaceholderData,
  // When provided, restricts the picker to roles whose id is in this list.
  // null/undefined means no filtering. An empty array means filter out everything,
  // leaving only the seeded `options` (typically the default value).
  allowedRoleIds?: ?Array<string | number>,
}>;

const roleOption = curry((includeAllCircleMembers, includeIndividualInitiative, role) => ({
  value: includeAllCircleMembers || includeIndividualInitiative ? `${role.id},${role.parent?.id}` : role.id,
  label: role.name,
  subtitle: role.parent ? I18n.t('shared.role_in_circle_simple', {circle_name: role.parent.name}) : '',
}))

const allCircleMembersOption = (parentRole) => ({
  value: `-2,${parentRole.id}`,
  label: I18n.t('shared.all_circle_members'),
  subtitle: I18n.t('shared.role_in_circle_simple', {circle_name: parentRole.name}),
})

const individualInitiativeOption = (parentRole, iiLabel) => ({
  value: `-1,${parentRole.id}`,
  label: iiLabel || I18n.t('activerecord.attributes.circle.v5.individual_action'),
  subtitle: I18n.t('shared.role_in_circle_simple', {circle_name: parentRole.name}),
})

function RailsRoleSelect({
  defaultValue, id, organizationId,
  isClearable, name, options, placeholderData, parentRoleId,
  personRoleIds, pageContext,
  showCircleName, includeAllCircleMembers, includeIndividualInitiative, individualInitiativeLabel,
  allowedRoleIds,
}: Props): Element<typeof RailsSelect> {
  const [optionsState, setOptionsState] = useState(options)

  const onInit = useCallback((selectWrapper) => {
    const roleInitEvent = new CustomEvent('role-init', {
      bubbles: true,
      cancelable: true,
      detail: {
        roleIdParentRoleId: defaultValue?.value,
        organizationId,
      },
    })
    selectWrapper.dispatchEvent(roleInitEvent)
  }, [defaultValue, organizationId])

  const onChange = useCallback((newOptions, currentWrapper) => {
    if (!newOptions)
      return
    // RailsSelect requires a change callback that handles multiselect.
    // RolsRoleSelect doesn't support multi, so it will always be a single option.
    // We do the array handling to make flow happy.
    const newOption = Array.isArray(newOptions) ? newOptions[0] : newOptions
    const value = newOption.value
    const roleIdParentRoleId = value.match(',') ? value : `${value},`

    const roleChangedEvent = new CustomEvent('role-changed', {
      bubbles: true,
      cancelable: true,
      detail: {
        roleIdParentRoleId,
        organizationId,
      },
    })
    currentWrapper.dispatchEvent(roleChangedEvent)
  }, [organizationId])

  useEffect(() => {
    // Get roleCache instance inside the effect to avoid hook rule violations
    const roleCache = RoleCache.instance(organizationId)
    let roles = []

    switch (pageContext) {
      case 'ROLES_PAGE':
        if (parentRoleId !== undefined && parentRoleId !== null)
          roles = roleCache.getSubRoles(parentRoleId)

        break
      case 'PERSON_PAGE':
        if (personRoleIds !== undefined)
          roles = roleCache.getRolesById(personRoleIds)
        break
      case 'PROJECT_PAGE':
        roles = roleCache.getAllRoles()
        break

      default:
        throw new Error(`Unknown page context: ${pageContext}`)
    }

    // Filter to roles the actor is allowed to assign to per backend policy.
    // null/undefined => no filter; an array (possibly empty) restricts to its members.
    if (Array.isArray(allowedRoleIds)) {
      const allowedSet = new Set(allowedRoleIds.map(String))
      roles = roles.filter((role) => allowedSet.has(String(role.id)))
    }

    // make sure to include original/default options
    let roleOptions = pipe(
      map(roleOption(includeAllCircleMembers, includeIndividualInitiative)),
      concat(options),
      sortBy(prop('label')),
    )(roles)

    if (includeAllCircleMembers) {
      const acmOptions = pipe(
        map(prop('parent')),
        uniqBy(prop('id')),
        reject(isNil),
        map((parentRole) => allCircleMembersOption(parentRole)),
      )(roles)

      roleOptions = uniqBy(prop('value'), [...acmOptions, ...roleOptions])
    }

    if (includeIndividualInitiative) {
      const iiOptions = pipe(
        map(prop('parent')),
        uniqBy(prop('id')),
        reject(isNil),
        map((parentRole) => individualInitiativeOption(parentRole, individualInitiativeLabel)),
      )(roles)

      roleOptions = uniqBy(prop('value'), [...roleOptions, ...iiOptions])
    }

    setOptionsState(roleOptions)
  }, [pageContext, parentRoleId, personRoleIds, organizationId, options,
    includeAllCircleMembers, includeIndividualInitiative, individualInitiativeLabel,
    allowedRoleIds,
  ])

  return (
    <RailsSelect
      defaultValue={defaultValue}
      showSubtitle={showCircleName}
      id={id}
      isMulti={false}
      isSearchable
      isClearable={isClearable}
      name={name}
      onChange={onChange}
      onInit={onInit}
      options={optionsState}
      placeholderData={placeholderData}
      wrapperClass={ROLE_SELECT_WRAPPER_CLASS}
    />
  )
}

export default RailsRoleSelect
